import axios from 'axios';

import settings from '~/settings';

import type { AxiosError, AxiosResponse, AxiosRequestConfig, ResponseType } from 'axios';

enum HTTP_METHODS {
  GET = 'GET',
  POST = 'POST',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  PUT = 'PUT',
}
type HTTP_METHOD = (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS];

const axiosInstance = axios.create({
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=utf-8',
    'X-CSRFToken': settings.csrfToken,
    'X-Requested-With': 'XMLHttpRequest',
  },
});

const getRequestConfig = <T>(url: string, data: T, options: AxiosRequestConfig = {}) => {
  const method = (options?.method as HTTP_METHOD) || HTTP_METHODS.GET;
  const dataOrParams = [HTTP_METHODS.GET, HTTP_METHODS.DELETE].includes(method) ? 'params' : 'data';

  const req = {
    url: url,
    method: method.toLowerCase(),
    responseType: 'json' as ResponseType,
    responseEncoding: 'utf-8',
    [dataOrParams]: data || {},
    ...options,
  };
  return req;
};

interface ApiError {
  error?: string;
  additional_info?: { reason?: string };
}

export interface AxiosCustomError<E> extends AxiosError<E> {
  data: E;
}

interface CustomAxiosResponse<T, E> extends AxiosResponse<T> {
  data: AxiosResponse<T>['data'] & Partial<E>;
}

const fetch = async <T, E = ApiError>(url: string, data: unknown = undefined, options: AxiosRequestConfig = {}) => {
  const params = getRequestConfig(url, data, options);

  try {
    const { status, data }: CustomAxiosResponse<T, E> = await axiosInstance(params);

    if (status < 200 || status >= 300) {
      throw data;
    }

    return data;
  } catch (error) {
    if (axios.isCancel(error)) {
      // Request is cancelled
    }

    if (axios.isAxiosError<E>(error)) {
      if (error.response) {
        throw error.response;
      }

      if (error.request) {
        throw error.request;
      }
    }

    throw error;
  }
};

export const post = async <T, E = ApiError>(
  url: string,
  data: unknown = undefined,
  options: AxiosRequestConfig = {},
) => {
  return await fetch<T, E>(url, data, { ...options, method: HTTP_METHODS.POST });
};

export const get = async <T, E = ApiError>(
  url: string,
  data: unknown = undefined,
  options: AxiosRequestConfig = {},
) => {
  return await fetch<T, E>(url, data, { ...options, method: HTTP_METHODS.GET });
};

export const patch = async <T, E = ApiError>(
  url: string,
  data: unknown = undefined,
  options: AxiosRequestConfig = {},
) => {
  return await fetch<T, E>(url, data, { ...options, method: HTTP_METHODS.PATCH });
};

export const put = async <T, E = ApiError>(
  url: string,
  data: unknown = undefined,
  options: AxiosRequestConfig = {},
) => {
  return await fetch<T, E>(url, data, { ...options, method: HTTP_METHODS.PUT });
};
